import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight, CalendarBlank, Flag, ListChecks, ShieldCheck, CalendarCheck, Wrench, Receipt, CreditCard, CurrencyDollar, Globe, Handshake } from "@phosphor-icons/react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/app/ui-bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { isStaffOnly } from "@/lib/roles";

const ICON_MAP = {
  Flag, ListChecks, ShieldCheck, CalendarCheck, Wrench, Receipt,
  CreditCard, CurrencyDollar, Globe, Handshake,
};

const KIND_LABEL = {
  compliance: "Compliance", deliverable: "Deliverable", task: "Task",
  appointment: "Appointment", asset: "Calibration", ar: "Receivable (AR)",
  ap: "Payable (AP)", tax: "Tax", donor: "Donor report", procurement: "Procurement",
};

const TONE_BG = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function fmtIso(d) { return d.toISOString().slice(0, 10); }

export default function CalendarPage() {
  const [view, setView] = useState("month"); // "month" | "agenda"
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = isStaffOnly(user);
  // Staff and Compliance Officers see only the month view — no cross-org agenda
  // (Compliance handles its own renewals via the Compliance module, and staff
  // see a personal-only calendar by design).
  const hideAgenda = isStaff || user?.role === "compliance_officer";

  // Force month view for roles that don't get agenda
  useEffect(() => {
    if (hideAgenda && view === "agenda") setView("month");
  }, [hideAgenda, view]);

  const range = useMemo(() => {
    if (view === "agenda") {
      const start = new Date();
      const end = new Date(); end.setDate(end.getDate() + 60);
      return { start: fmtIso(start), end: fmtIso(end) };
    }
    // Month view — pad with surrounding days
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const padStart = new Date(first); padStart.setDate(first.getDate() - first.getDay());
    const padEnd = new Date(last); padEnd.setDate(last.getDate() + (6 - last.getDay()));
    return { start: fmtIso(padStart), end: fmtIso(padEnd) };
  }, [view, cursor]);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/calendar/events", { params: range });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [range.start, range.end]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (kindFilter === "all") return data.events;
    return data.events.filter((e) => e.kind === kindFilter);
  }, [data, kindFilter]);

  const eventsByDate = useMemo(() => {
    const m = {};
    for (const e of filtered) {
      (m[e.date] = m[e.date] || []).push(e);
    }
    return m;
  }, [filtered]);

  const goto = (e) => {
    if (!e.link) return;
    const link = e.link.startsWith("/admin") ? e.link.slice(6) : e.link;
    navigate(link);
  };

  // Build month grid
  const monthGrid = useMemo(() => {
    if (view !== "month") return [];
    const padStart = new Date(range.start);
    const padEnd = new Date(range.end);
    const days = [];
    let d = new Date(padStart);
    while (d <= padEnd) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [view, range.start, range.end]);

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const today = data?.today || fmtIso(new Date());

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow={isStaff ? "My Workspace" : "Operations"}
        title={isStaff ? "My Calendar" : "Calendar & Deadlines"}
        description={isStaff
          ? "Your personal schedule — only tasks assigned to you, your appointments and your project deliverables. No company-wide records."
          : "Every dated event across the organisation in one timeline — milestones, deliverables, compliance renewals, payments, calibrations, donor reports."}
        actions={
          <div className="flex gap-2">
            <Button variant={view === "month" ? "default" : "outline"} size="sm" onClick={() => setView("month")} data-testid="cal-view-month">Month</Button>
            {!hideAgenda && (
              <Button variant={view === "agenda" ? "default" : "outline"} size="sm" onClick={() => setView("agenda")} data-testid="cal-view-agenda">Agenda</Button>
            )}
          </div>
        }
      />

      <div className="space-y-5 p-5 md:p-8">
        <div className="dc-card p-4 flex flex-wrap items-center gap-3">
          {view === "month" && (
            <>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev"><CaretLeft size={14} /></Button>
              <div className="font-display text-lg font-semibold text-slate-900 w-44 text-center" data-testid="cal-month-label">{monthLabel}</div>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next"><CaretRight size={14} /></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
            </>
          )}
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="cal-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
            {busy ? "loading…" : `${filtered.length} events`}
          </span>
        </div>

        {view === "month" ? (
          <div className="dc-card overflow-hidden" data-testid="cal-month-grid">
            {/* Weekday header */}
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="border-r border-slate-200 px-2 py-2 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 text-center last:border-r-0">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {monthGrid.map((d, idx) => {
                const iso = fmtIso(d);
                const dayEvents = eventsByDate[iso] || [];
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = iso === today;
                return (
                  <div
                    key={iso}
                    className={`min-h-[100px] border-b border-r border-slate-100 p-1.5 text-xs last-in-row:border-r-0 ${
                      !inMonth ? "bg-slate-50/50" : "bg-white"
                    } ${(idx + 1) % 7 === 0 ? "border-r-0" : ""}`}
                    data-testid={`cal-day-${iso}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-mono-alt text-[11px] ${
                        isToday ? "bg-[#1E5CBA] text-white font-bold" : inMonth ? "text-slate-700" : "text-slate-400"
                      }`}>
                        {d.getDate()}
                      </span>
                      {dayEvents.length > 0 && (
                        <span className="font-mono-alt text-[9px] text-slate-400">{dayEvents.length}</span>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {dayEvents.slice(0, 3).map((e, i) => {
                        const Icon = ICON_MAP[e.icon] || CalendarBlank;
                        return (
                          <button
                            key={`${e.kind}-${e.id || i}`}
                            onClick={() => goto(e)}
                            className={`flex w-full items-center gap-1 truncate rounded border px-1.5 py-0.5 text-left text-[10px] ${TONE_BG[e.tone] || TONE_BG.slate}`}
                            data-testid={`cal-event-${iso}-${i}`}
                            title={e.title}
                          >
                            <Icon size={9} />
                            <span className="truncate">{e.title}</span>
                          </button>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-slate-500">+{dayEvents.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="dc-card overflow-hidden" data-testid="cal-agenda">
            {filtered.length === 0 ? (
              <EmptyState icon={CalendarBlank} title="No upcoming events" description="No deadlines in the next 60 days for this filter." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((e, i) => {
                  const Icon = ICON_MAP[e.icon] || CalendarBlank;
                  return (
                    <li key={`${e.kind}-${e.id || i}`}>
                      <button
                        onClick={() => goto(e)}
                        className="flex w-full items-center gap-4 px-5 py-3 text-left transition hover:bg-slate-50"
                        data-testid={`agenda-row-${i}`}
                      >
                        <div className="w-24 text-right">
                          <div className="font-display text-sm font-semibold text-slate-900">{formatDate(e.date)}</div>
                          <div className="font-mono-alt text-[9px] uppercase tracking-widest text-slate-500">
                            {new Date(e.date).toLocaleDateString(undefined, { weekday: "short" })}
                          </div>
                        </div>
                        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full border ${TONE_BG[e.tone] || TONE_BG.slate}`}>
                          <Icon size={12} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{e.title}</div>
                          <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                            {KIND_LABEL[e.kind] || e.kind}{e.is_milestone ? " · MILESTONE" : ""}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
